
import type { FetchResult } from 'apollo-link'
import { defineComponent, type PropType } from 'vue'

import CreateManualTopupMutation from '~/apollo/mutations/owner/createManualTopup.gql'
import type { CreateManualTopupMutation as CreateManualTopupMutationData } from '~/types/types'
import { getCardImage } from '~/utils/billing'

export interface AddCreditPaymentMethod {
  id: string
  brand: string
  brandLabel: string
  lastFour: string
  expiryLabel: string
  expiryShort: string
  isDefault: boolean
}

export default defineComponent({
  name: 'V2AddCreditModal',
  props: {
    ownerId: {
      type: String,
      required: true
    },
    paymentMethods: {
      type: Array as PropType<AddCreditPaymentMethod[]>,
      required: true
    }
  },
  data() {
    return {
      amount: '' as string,
      selectedMethodId: null as string | null,
      dropdownOpen: false,
      processing: false,
      submitError: null as string | null
    }
  },
  computed: {
    selectedMethod(): AddCreditPaymentMethod | null {
      if (!this.selectedMethodId) return null
      return this.paymentMethods.find((m) => m.id === this.selectedMethodId) ?? null
    },
    parsedAmountCents(): number {
      const parsed = parseFloat(this.amount)
      return isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : 0
    },
    amountError(): string | null {
      if (!this.amount) return null
      if (this.amount && this.parsedAmountCents === 0) return 'Please enter a valid amount'
      if (this.parsedAmountCents > 0 && this.parsedAmountCents < 500) return 'Minimum amount is $5'
      return null
    },
    canSubmit(): boolean {
      return this.parsedAmountCents >= 500 && Boolean(this.selectedMethodId) && !this.processing
    }
  },
  mounted() {
    const defaultMethod = this.paymentMethods.find((m) => m.isDefault)
    if (defaultMethod) {
      this.selectedMethodId = defaultMethod.id
    } else if (this.paymentMethods.length > 0) {
      this.selectedMethodId = this.paymentMethods[0].id
    }
  },
  methods: {
    cardImage: getCardImage,
    closeDropdown(): void {
      this.dropdownOpen = false
    },
    formatAmount(): void {
      const parsed = parseFloat(this.amount)
      if (isFinite(parsed) && parsed > 0) {
        this.amount = parsed.toFixed(2)
      }
    },
    focusOption(index: number): void {
      const ref = this.$refs[`option-${index}`] as HTMLElement[] | HTMLElement | undefined
      const el = Array.isArray(ref) ? ref[0] : ref
      el?.focus()
    },
    onTriggerKeydown(event: KeyboardEvent): void {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault()
          if (!this.dropdownOpen) {
            this.dropdownOpen = true
            this.$nextTick(() => {
              const idx = this.paymentMethods.findIndex((m) => m.id === this.selectedMethodId)
              this.focusOption(idx >= 0 ? idx : 0)
            })
          }
          break
        case 'Escape':
          if (this.dropdownOpen) {
            event.preventDefault()
            event.stopPropagation()
            this.dropdownOpen = false
          }
          break
        default:
          break
      }
    },
    onListboxKeydown(event: KeyboardEvent): void {
      const focusedIndex = Array.from((event.currentTarget as HTMLElement).children).indexOf(
        document.activeElement as HTMLElement
      )

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          if (focusedIndex < this.paymentMethods.length - 1) {
            this.focusOption(focusedIndex + 1)
          }
          break
        case 'ArrowUp':
          event.preventDefault()
          if (focusedIndex > 0) {
            this.focusOption(focusedIndex - 1)
          }
          break
        case 'Home':
          event.preventDefault()
          this.focusOption(0)
          break
        case 'End':
          event.preventDefault()
          this.focusOption(this.paymentMethods.length - 1)
          break
        case 'Escape':
          event.preventDefault()
          event.stopPropagation()
          this.dropdownOpen = false
          ;(this.$refs.triggerRef as HTMLElement)?.focus()
          break
        default:
          break
      }
    },
    selectMethod(method: AddCreditPaymentMethod): void {
      this.selectedMethodId = method.id
      this.dropdownOpen = false
      ;(this.$refs.triggerRef as HTMLElement)?.focus()
    },
    async submit(): Promise<void> {
      if (!this.canSubmit || !this.selectedMethodId) return

      this.processing = true
      this.submitError = null

      try {
        const response = (await this.$applyGraphqlMutation(CreateManualTopupMutation, {
          ownerId: this.ownerId,
          amountCents: this.parsedAmountCents,
          paymentMethodId: this.selectedMethodId
        })) as FetchResult<CreateManualTopupMutationData>

        if (response.data?.createManualTopup?.invoiceId) {
          this.$emit('credit-added')
          return // keep processing = true; parent will close modal after polling
        }
        this.submitError = 'Failed to add credit. Please try again.'
      } catch (err) {
        this.submitError = (err as Error).message ?? 'An unexpected error occurred.'
      }
      this.processing = false
    },
    close(): void {
      if (this.processing) return
      this.$emit('close')
    }
  }
})
